import { useState, useEffect } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, User, MapPin, Heart, Camera, LogOut, Lock, DollarSign, LifeBuoy, Trash2, Globe, Play } from "lucide-react";
import Navigation from "@/components/Navigation";
import { useAppMode } from "@/context/AppModeContext";
import PhotoGallery from "@/components/PhotoGallery";
import PayPalSettings from "@/components/PayPalSettings";
import ReferralCard from "@/components/ReferralCard";
import RelationshipCard from "@/components/RelationshipCard";
import PhotoVerificationCard from "@/components/PhotoVerificationCard";
import RatingsPrivacyCard from "@/components/RatingsPrivacyCard";
import IncognitoCard from "@/components/IncognitoCard";
import VoucherCard from "@/components/VoucherCard";
import SupportTicketDialog from "@/components/SupportTicketDialog";
import SocialInviteTracker from "@/components/SocialInviteTracker";
import { useLocale } from "@/i18n/LocaleContext";
import { COUNTRIES, LANGS } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { isEarner } = useAppMode();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    if (user?.country && user.country !== country) setCountry(user.country);
    if (user?.language && user.language !== lang) setLang(user.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country, user?.language]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { t, lang, setLang, country, setCountry, currency } = useLocale();

  const handleCountryChange = (code) => {
    setCountry(code);
    const info = COUNTRIES.find((c) => c.code === code);
    api.put("/users/profile", { country: code, language: info?.lang, currency: info?.currency }).catch(() => {});
  };

  const handleLanguageChange = (code) => {
    setLang(code);
    api.put("/users/profile", { language: code }).catch(() => {});
  };
  const [formData, setFormData] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    age: user?.age || "",
    location: user?.location || "",
    interests: user?.interests?.join(", ") || ""
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t("set.tImgFile"));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("set.tImgSize"));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload/profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update user object with new photo
      updateUser({ ...user, picture: response.data.url, photos: [response.data.url] });
      toast.success(t("set.tPhotoUpdated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("set.tPhotoFail"));
    } finally {
      setUploading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t("set.tPwMismatch"));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(t("set.tPwShort"));
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      toast.success(t("set.tPwChanged"));
      setPasswordDialog(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("set.tPwFail"));
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/auth/me');
      updateUser(response.data);
    } catch (error) {
      console.error("Failed to fetch profile", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        interests: formData.interests.split(",").map(i => i.trim()).filter(Boolean)
      };

      const response = await api.put("/users/profile", data);
      updateUser(response.data);
      toast.success(t("set.tProfileUpdated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("set.tProfileFail"));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success(t("set.tLoggedOut"));
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/account/deactivate", { password: deletePassword || null });
      setDeleteDialog(false);
      navigate("/");
      await logout();
      toast.success(t("set.tDeactivated"), { duration: 8000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("set.tDeleteFail"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <Navigation />
      
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            className="mb-6 text-muted-foreground hover:text-white -ml-2"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.back")}
          </Button>

          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight mb-6">{t("settings.title")}</h1>

          {/* Profile photo */}
          <div className="glass-card p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src={user?.photos?.[0] || user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=${avatarBg()}&color=fff&size=200`}
                  alt={user?.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                />
                <label 
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </label>
              </div>
              <div>
                <h2 className="text-xl font-semibold">{user?.name}</h2>
                <p className="text-muted-foreground text-sm">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("settings.photoHint")}</p>
              </div>
            </div>
          </div>

          {/* Profile form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                {t("settings.profileInfo")}
              </h3>

              <div>
                <Label htmlFor="name">{t("settings.displayName")}</Label>
                <Input
                  id="name"
                  className="mt-1 bg-white/5 border-white/10"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label htmlFor="age">{t("settings.age")}</Label>
                <Input
                  id="age"
                  type="number"
                  min="18"
                  max="100"
                  className="mt-1 bg-white/5 border-white/10"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  data-testid="age-input"
                />
              </div>

              <div>
                <Label htmlFor="location">{t("settings.location")}</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="location"
                    className="pl-10 bg-white/5 border-white/10"
                    placeholder={t("settings.locationPh")}
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    data-testid="location-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="bio">{t("settings.bio")}</Label>
                <Textarea
                  id="bio"
                  className="mt-1 bg-white/5 border-white/10 min-h-[100px]"
                  placeholder={t("settings.bioPh")}
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  data-testid="bio-input"
                />
              </div>

              <div>
                <Label htmlFor="interests">{t("settings.interests")}</Label>
                <div className="relative mt-1">
                  <Heart className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="interests"
                    className="pl-10 bg-white/5 border-white/10"
                    placeholder={t("settings.interestsPh")}
                    value={formData.interests}
                    onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                    data-testid="interests-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t("settings.interestsHint")}</p>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="save-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                t("settings.saveChanges")
              )}
            </Button>
          </form>

          {/* PayPal Payout Settings - Women Only */}
          {isEarner && (
            <div className="mt-8 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-2xl p-6 border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{t("settings.paypalTitle")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.paypalDesc")}
                  </p>
                </div>
              </div>

              <PayPalSettings user={user} onUpdate={fetchUserProfile} />
            </div>
          )}

          {/* Official Relationship - all users */}
          <RelationshipCard />

          {/* Invite & Earn - all users */}
          <div className="mt-8">
            <ReferralCard />
          </div>

          {/* Photo Verification - all users */}
          <PhotoVerificationCard />

          {/* Champion voucher - all users */}
          <VoucherCard showRedeem />

          {/* Social Secret Admirer request tracking */}
          <SocialInviteTracker />

          {/* Replay the 60-second test drive tour */}
          <div className="mt-8 glass-card p-6 rounded-2xl flex items-center justify-between gap-4" data-testid="tour-replay-card">
            <div>
              <h2 className="text-lg font-semibold">{t("tour.replayTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("tour.replayDesc")}</p>
            </div>
            <Button
              variant="outline"
              className="shrink-0"
              data-testid="tour-replay-btn"
              onClick={async () => {
                try {
                  await api.post("/test-drive/restart");
                  localStorage.removeItem("luxe_tour_seen");
                  window.dispatchEvent(new Event("tour-restart"));
                  navigate("/discover");
                } catch {}
              }}
            >
              <Play className="w-4 h-4 mr-2" /> {t("tour.replayBtn")}
            </Button>
          </div>

          {/* Date Ratings Privacy - all users */}
          <RatingsPrivacyCard />

          {/* Incognito Mode - all users */}
          <IncognitoCard />

          {/* Photo Gallery */}
          <PhotoGallery 
            photos={user?.photos || []} 
            videos={user?.videos || []}
            onPhotosUpdate={fetchUserProfile}
          />

          {/* Country & Language */}
          <div className="mt-8 glass-card p-6 rounded-2xl" data-testid="country-language-card">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-primary" />
              {t("settings.countryLanguage")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">{t("settings.country")}</Label>
                <Select value={country} onValueChange={handleCountryChange}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`country-option-${c.code}`}>
                        {c.flag} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t("settings.language")}</Label>
                <Select value={lang} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {Object.entries(LANGS).map(([code, label]) => (
                      <SelectItem key={code} value={code} data-testid={`language-option-${code}`}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {t("settings.currency")}: <span className="text-white font-medium" data-testid="currency-display">{currency}</span> — {t("settings.currencyNote")}
            </p>
          </div>

          {/* Account actions */}
          <div className="mt-8 space-y-3">
            <Button
              variant="outline"
              className="w-full border-white/10 hover:bg-white/5 text-muted-foreground"
              onClick={() => setPasswordDialog(true)}
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {t("settings.changePassword")}
            </Button>
            
            <Button
              variant="outline"
              className="w-full border-white/10 hover:bg-white/5 text-muted-foreground"
              onClick={() => setSupportOpen(true)}
              data-testid="support-tickets-btn"
            >
              <LifeBuoy className="w-4 h-4 mr-2" />
              {t("settings.refunds")}
            </Button>
            
            <Button
              variant="outline"
              className="w-full border-white/10 hover:bg-white/5 text-muted-foreground"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t("settings.logout")}
            </Button>
          </div>

          {/* Danger zone */}
          <div className="mt-8 rounded-2xl border border-red-500/25 bg-red-500/5 p-6" data-testid="danger-zone">
            <h3 className="font-semibold text-red-400 flex items-center gap-2 mb-2">
              <Trash2 className="w-4 h-4" /> {t("settings.dangerZone")}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              {t("settings.dangerDesc")}
            </p>
            <Button
              variant="outline"
              className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              onClick={() => setDeleteDialog(true)}
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("settings.deleteAccount")}
            </Button>
          </div>
        </div>
      </main>

      <SupportTicketDialog open={supportOpen} onOpenChange={setSupportOpen} />

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-card border-white/10" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-red-400">{t("settings.deleteTitle")}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>• {t("settings.deleteB1")}</p>
            <p>• {t("settings.deleteB2")}</p>
            <p>• {t("settings.deleteB3")}</p>
          </div>
          <div>
            <Label htmlFor="delete-password">{t("settings.confirmPasswordLabel")}</Label>
            <Input
              id="delete-password"
              type="password"
              className="mt-1 bg-white/5 border-white/10"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              data-testid="delete-account-password"
            />
            <p className="text-xs text-muted-foreground mt-1">{t("settings.googleBlank")}</p>
          </div>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" onClick={() => setDeleteDialog(false)} data-testid="cancel-delete-account-btn">
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting}
              data-testid="confirm-delete-account-btn"
            >
              {deleting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                t("settings.deactivateDelete")
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t("settings.changePassword")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="current-password">{t("settings.currentPassword")}</Label>
              <Input
                id="current-password"
                type="password"
                className="mt-1 bg-white/5 border-white/10"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="new-password">{t("settings.newPassword")}</Label>
              <Input
                id="new-password"
                type="password"
                className="mt-1 bg-white/5 border-white/10"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">{t("settings.confirmNewPassword")}</Label>
              <Input
                id="confirm-password"
                type="password"
                className="mt-1 bg-white/5 border-white/10"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={changingPassword}
            >
              {changingPassword ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                t("settings.changePassword")
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
